/**
 * Which Overdue/Stale rows the Inbox tab renders, and which open close
 * proposals are decided on a Stale row instead of in the Proposals list
 * (A7Y-4733: each row in one place). Shared by `OverdueStale`, `Proposals` and
 * `HelmPage`'s tab count so the three can never disagree.
 */
import type { HelmCommitment, HelmProposal } from '@a7y/platform-health/helm';

/** Stale/overdue keys that resolve into `commitments` and are not suppressed —
 * exactly the rows `OverdueStale` renders. */
export function visibleCommitmentKeys(
  keys: readonly string[],
  commitments: readonly HelmCommitment[],
  suppressedKeys: ReadonlySet<string>
): string[] {
  const known = new Set(commitments.map((c) => c.key));
  return keys.filter((key) => known.has(key) && !suppressedKeys.has(key));
}

/** An open R5 close proposal for one of `staleKeys` (it cites the stale row's
 * key in `basis`). Decided on the Stale row, never in the Proposals list. */
export function isStaleCloseProposal(proposal: HelmProposal, staleKeys: ReadonlySet<string>) {
  return (
    proposal.kind === 'roster_close' &&
    proposal.status === 'open' &&
    proposal.decision == null &&
    proposal.basis.some((key) => staleKeys.has(key))
  );
}

/** A proposal Inbox lists under Refused: gone stale, or decided on an earlier revision. */
export function isRefusedProposal(proposal: HelmProposal): boolean {
  return proposal.status === 'stale' || proposal.refused != null;
}

/**
 * The record rows (`evidence.ref.id`) whose HELM proposal Inbox lists under
 * Refused and that HELM has not proposed again (A7Y-5873): Today does not count
 * them as due until it does.
 */
export function refusedRosterIds(proposals: readonly HelmProposal[]): Set<string> {
  const rowId = (p: HelmProposal) =>
    p.evidence.ref.kind === 'roster_row' ? p.evidence.ref.id : null;
  const proposedAgain = new Set(
    proposals.filter((p) => p.status === 'open' && !isRefusedProposal(p)).map(rowId)
  );
  return new Set(
    proposals
      .filter(isRefusedProposal)
      .map(rowId)
      .filter((id): id is string => id !== null && !proposedAgain.has(id))
  );
}
